import { DataProvider, fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { API_URL } from './config';

/**
 * Maps react-admin queries to the FastAPI endpoints.
 */
const dataProvider: DataProvider = {    getList: async (resource, params) => {
        // Extract sorting and pagination parameters
        const { page = 1, perPage = 10 } = params.pagination || {};
        const { field = 'id', order = 'ASC' } = params.sort || {};
        
        // FastAPI uses skip/limit for pagination
        const skip = (page - 1) * perPage;
        const limit = perPage;
        
        // Prepare the query parameters
        const query = {
            skip: skip.toString(),
            limit: limit.toString(),
            sort_by: field,
            sort_order: order.toLowerCase(),
            ...params.filter,
        };

        console.log(`${resource} query parameters:`, query);
          
        // Build the URL with query string
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : resource === 'queue'
            ? `${API_URL}/queue/my-tasks`
            : `${API_URL}/${resource}`;
        
        const url = `${baseUrl}?${stringify(query)}`;
        console.log(`Fetching ${resource} list from URL: ${url}`);
        
        // Get the headers with authorization
        const options = getOptionsWithToken();
        console.log('Request options:', {...options, headers: {...options.headers, Authorization: 'Bearer [REDACTED]'}});
          try {
            // Fetch the data
            const response = await fetchUtils.fetchJson(url, options);
            
            // Debug the response
            console.log(`${resource} API Response:`, response);
            console.log(`${resource} data:`, response.json);
            console.log(`${resource} total count:`, response.headers.get('X-Total-Count'));
            console.log(`${resource} all headers:`, response.headers);

            // First, ensure we have a data array to work with
            let dataArray = response.json;
            
            // Handle non-array responses
            if (!Array.isArray(dataArray)) {
                console.warn(`${resource} response is not an array:`, dataArray);
                
                // If it's an object, convert to array with one item
                if (dataArray && typeof dataArray === 'object') {
                    dataArray = [dataArray];
                } else {
                    // If it's null or undefined, create an empty array
                    dataArray = [];
                }
            }
            
            // Get the total count from headers or use array length as fallback
            const totalCountHeader = response.headers.get('X-Total-Count');
            console.log(`${resource} X-Total-Count header:`, totalCountHeader);
            
            // Parse the total count, with fallback to array length
            const total = totalCountHeader ? parseInt(totalCountHeader, 10) : dataArray.length;
            console.log(`${resource} calculated total:`, total);
            
            // Return data in react-admin format
            return {
                data: dataArray,
                total: total,
            };
        } catch (error) {
            console.error(`Error fetching ${resource} list:`, error);
            throw error;
        }
    },    getOne: async (resource, params) => {
        // Build URL based on resource
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : resource === 'queue'
            ? `${API_URL}/queue`
            : `${API_URL}/${resource}`;
            
        const url = resource === 'queue' 
            ? `${baseUrl}/${params.id}/info`
            : `${baseUrl}/${params.id}`;
        const options = getOptionsWithToken();
        
        console.log(`Fetching ${resource} with ID ${params.id} from ${url}`);
        
        try {
            const response = await fetchUtils.fetchJson(url, options);
            console.log(`${resource} data:`, response.json);
            
            // Ensure we have a valid response object
            const responseData = response.json && typeof response.json === 'object' 
                ? response.json 
                : { id: params.id };
                
            return {
                data: responseData,
            };
        } catch (error) {
            console.error(`Error fetching ${resource}:`, error);
            throw error;
        }
    },

    getMany: async (resource, params) => {
        // Build URL based on resource
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : `${API_URL}/${resource}`;

        // Use repeated params: ?id=1&id=2&id=3 — this is what FastAPI's List[int] expects
        const url = `${baseUrl}?${params.ids.map(id => `id=${encodeURIComponent(id)}`).join('&')}`;
        const options = getOptionsWithToken();
        
        console.log(`Fetching multiple ${resource} with IDs ${params.ids} from ${url}`);
        
        try {
            const response = await fetchUtils.fetchJson(url, options);
            console.log(`${resource} data:`, response.json);
            
            // Ensure we have an array response
            let dataArray = response.json;
            if (!Array.isArray(dataArray)) {
                console.warn(`${resource} response is not an array:`, dataArray);
                if (dataArray && typeof dataArray === 'object') {
                    dataArray = [dataArray];
                } else {
                    dataArray = [];
                }
            }
            
            return {
                data: dataArray,
            };
        } catch (error) {
            console.error(`Error fetching ${resource}:`, error);
            throw error;
        }
    },    getManyReference: async (resource, params) => {
        const { page = 1, perPage = 10 } = params.pagination || {};
        const { field = 'id', order = 'ASC' } = params.sort || {};
        
        const skip = (page - 1) * perPage;
        const limit = perPage;
        
        const query = {
            skip: skip.toString(),
            limit: limit.toString(),
            sort_by: field,
            sort_order: order.toLowerCase(),
            ...params.filter,
            [params.target]: params.id,
        };
        
        // Build URL based on resource
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : `${API_URL}/${resource}`;
            
        const url = `${baseUrl}?${stringify(query)}`;
        const options = getOptionsWithToken();
        
        console.log(`Fetching ${resource} references with target ${params.target}=${params.id} from ${url}`);
        
        try {
            const response = await fetchUtils.fetchJson(url, options);
            console.log(`${resource} reference data:`, response.json);
            console.log(`${resource} reference total count:`, response.headers.get('X-Total-Count'));
            
            // Ensure we have an array response
            let dataArray = response.json;
            if (!Array.isArray(dataArray)) {
                console.warn(`${resource} response is not an array:`, dataArray);
                if (dataArray && typeof dataArray === 'object') {
                    dataArray = [dataArray];
                } else {
                    dataArray = [];
                }
            }
            
            // Get the total count from headers or use array length as fallback
            const totalCountHeader = response.headers.get('X-Total-Count');
            const total = totalCountHeader ? parseInt(totalCountHeader, 10) : dataArray.length;
            
            return {
                data: dataArray,
                total: total,
            };
        } catch (error) {
            console.error(`Error fetching ${resource} references:`, error);
            throw error;
        }
    },create: async (resource, params) => {
        // Build URL based on resource
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : `${API_URL}/${resource}`;
            
        const url = baseUrl;
        const options = {
            ...getOptionsWithToken(),
            method: 'POST',
            body: JSON.stringify(params.data),
        };
        
        console.log(`Creating ${resource} with data:`, params.data);
        console.log(`POST to URL: ${url}`);
        
        try {
            const response = await fetchUtils.fetchJson(url, options);
            console.log(`${resource} created:`, response.json);
            
            // Return the created record with its ID
            return {
                data: { ...params.data, ...response.json },
            };
        } catch (error) {
            console.error(`Error creating ${resource}:`, error);
            throw error;
        }
    },    update: async (resource, params) => {
        // Build URL based on resource
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : `${API_URL}/${resource}`;
            
        const url = `${baseUrl}/${params.id}`;
        const options = {
            ...getOptionsWithToken(),
            method: 'PUT',
            body: JSON.stringify(params.data),
        };
        
        console.log(`Updating ${resource} with ID ${params.id}, data:`, params.data);
        console.log(`PUT to URL: ${url}`);
        
        try {
            const response = await fetchUtils.fetchJson(url, options);
            console.log(`${resource} updated:`, response.json);
            
            // Return data with merged response and original params
            return {
                data: { ...params.data, ...response.json },
            };
        } catch (error) {
            console.error(`Error updating ${resource}:`, error);
            throw error;
        }
    },

    updateMany: async (resource, params) => {
        console.log(`Updating multiple ${resource} with IDs ${params.ids}, data:`, params.data);
        
        try {
            // Build URL based on resource
            const baseUrl = resource === 'users' || resource === 'invitations'
                ? `${API_URL}/auth/${resource}`
                : resource === 'oauth-applications'
                ? `${API_URL}/auth/oauth/applications`
                : `${API_URL}/${resource}`;
            
            const responses = await Promise.all(
                params.ids.map(id => 
                    fetchUtils.fetchJson(`${baseUrl}/${id}`, {
                        ...getOptionsWithToken(),
                        method: 'PUT',
                        body: JSON.stringify(params.data),
                    })
                )
            );
            
            console.log(`Updated ${responses.length} ${resource} records`);
            
            return {
                data: params.ids,
            };
        } catch (error) {
            console.error(`Error updating multiple ${resource}:`, error);
            throw error;
        }
    },    delete: async (resource, params) => {
        // Build URL based on resource
        const baseUrl = resource === 'users' || resource === 'invitations'
            ? `${API_URL}/auth/${resource}`
            : resource === 'oauth-applications'
            ? `${API_URL}/auth/oauth/applications`
            : resource === 'queue'
            ? `${API_URL}/queue`
            : `${API_URL}/${resource}`;
            
        const url = `${baseUrl}/${params.id}`;
        const options = {
            ...getOptionsWithToken(),
            method: 'DELETE',
        };
        
        console.log(`Deleting ${resource} with ID ${params.id}`);
        console.log(`DELETE to URL: ${url}`);
        
        try {
            const response = await fetchUtils.fetchJson(url, options);
            console.log(`${resource} deleted:`, response.json);
            
            return {
                data: { id: params.id, ...response.json },
            };
        } catch (error) {
            console.error(`Error deleting ${resource}:`, error);
            throw error;
        }
    },

    deleteMany: async (resource, params) => {
        console.log(`Deleting multiple ${resource} with IDs ${params.ids}`);
        
        try {
            // Build URL based on resource
            const baseUrl = resource === 'users' || resource === 'invitations'
                ? `${API_URL}/auth/${resource}`
                : resource === 'oauth-applications'
                ? `${API_URL}/auth/oauth/applications`
                : `${API_URL}/${resource}`;
            
            await Promise.all(
                params.ids.map(id => 
                    fetchUtils.fetchJson(`${baseUrl}/${id}`, {
                        ...getOptionsWithToken(),
                        method: 'DELETE',
                    })
                )
            );
            
            console.log(`Deleted ${params.ids.length} ${resource} records`);
            
            return {
                data: params.ids,
            };
        } catch (error) {
            console.error(`Error deleting multiple ${resource}:`, error);
            throw error;
        }
    },
};

/**
 * Helper function to get HTTP options with authentication token
 */
const getOptionsWithToken = () => {
    const token = localStorage.getItem('token');
    const tokenType = localStorage.getItem('tokenType') || 'bearer';
    
    if (!token) {
        console.warn('No authentication token found in localStorage');
        return {
            headers: new Headers({
                'Content-Type': 'application/json',
            }),
        };
    }
    
    console.log('Authentication token found, adding to request headers');
    
    return {
        headers: new Headers({
            'Authorization': `${tokenType} ${token}`,
            'Content-Type': 'application/json',
        }),
    };
};

export default dataProvider;
